import {type ComAtprotoLabelDefs} from '@atproto/api'

import {WSOCIAL_LABELER_DID} from '#/lib/constants'
import {type AccountType} from '#/wsocial/types/profile'

/**
 * Maps a `wsocial-*` Ozone label value to the canonical `AccountType`
 * vocabulary. Keep this in sync with `WSOCIAL_ACCOUNT_TYPE_LABELS` in
 * `#/lib/constants` and with the equivalent, independently-maintained
 * `findActiveWsocialLabel` utility in `packages/bsky/src/wsocial-verification.ts`
 * (see VERIFICATION-BADGE-REPAIR-PLAN.md Phase 3 / Phase 4) — this frontend
 * bundle can't import that package directly, so the checks are duplicated
 * here rather than shared.
 */
const WSOCIAL_LABEL_VAL_TO_ACCOUNT_TYPE: Record<string, AccountType> = {
  'wsocial-personal': 'personal',
  'wsocial-org': 'organization',
  'wsocial-bot': 'bot',
  'wsocial-service': 'service',
  'wsocial-test': 'test',
}

/**
 * Scans `labels` for an active (non-negated, non-expired) `wsocial-*` label
 * issued by `wsocialLabelerDid` and returns the corresponding `AccountType`,
 * or `undefined` if none is found. Treating an expired label as absent is a
 * deliberate decision — see Phase 3 of VERIFICATION-BADGE-REPAIR-PLAN.md.
 */
export function findWsocialAccountType(
  labels: readonly ComAtprotoLabelDefs.Label[] | undefined,
  wsocialLabelerDid: string | undefined = WSOCIAL_LABELER_DID,
): AccountType | undefined {
  if (!labels || !wsocialLabelerDid) return undefined
  const validLabel = labels
    .filter(
      label =>
        label.src === wsocialLabelerDid &&
        !label.neg &&
        !(label.exp && new Date(label.exp).getTime() <= Date.now()),
    )
    .find(label => WSOCIAL_LABEL_VAL_TO_ACCOUNT_TYPE[label.val])

  const accountType = validLabel
    ? WSOCIAL_LABEL_VAL_TO_ACCOUNT_TYPE[validLabel.val]
    : undefined
  if (accountType) return accountType
  return undefined
}
